import { useMemo } from 'react';
import CircularSelect from '../../../../../../common/MagicUpload/components/CircularSelect/CircularSelect';
import Button from '../../../../../../common/_custom/Button/Button';
import { OrgEntity } from '../../../../../../store/dashboard/financials/entityRationaleServices';
import { CLASSIFICATION_OPTIONS } from './constants';
import styles from './DrawerBulkEditFooter.module.scss';

export interface DrawerBulkEdit {
    /** Existing entity picked from the org list. */
    targetEntityId?: number;
    targetEntityName?: string;
    /** Add-New-Entity path: true when the user typed a new name instead of picking an existing one. */
    createEntity?: boolean;
    newEntityName?: string;
    rationale?: string;
    classificationId?: number;
}

interface Props {
    selectedCount: number;
    /** True when selectedCount exceeds the per-request limit; Save is disabled and a message is shown. */
    overLimit?: boolean;
    edit: DrawerBulkEdit;
    orgEntities: OrgEntity[];
    rationaleOptions: string[];
    saving: boolean;
    onEditChange: (patch: Partial<DrawerBulkEdit>) => void;
    onCancel: () => void;
    onSave: () => void;
}

const CLASSIFICATION_LABELS = CLASSIFICATION_OPTIONS.map((o) => o.label);

/** Normalise an entity name for matching: lowercase + collapse whitespace. Mirrors the
 *  common-case portion of UW's normalizeEntitySuffix without replicating suffix rules. */
function normalizeForMatch(s: string): string {
    return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function DrawerBulkEditFooter({
    selectedCount,
    overLimit = false,
    edit,
    orgEntities,
    rationaleOptions,
    saving,
    onEditChange,
    onCancel,
    onSave,
}: Props) {
    const entityOptions = useMemo(() => orgEntities.map((e) => e.displayName ?? ''), [orgEntities]);

    const handleEntitySelect = async (value: string): Promise<boolean> => {
        const trimmed = value.trim();
        const normalised = normalizeForMatch(trimmed);
        const matched =
            orgEntities.find((e) => e.normalizedName === normalised) ??
            orgEntities.find((e) => normalizeForMatch(e.displayName) === normalised);
        if (matched) {
            onEditChange({
                targetEntityId: matched.entityId,
                targetEntityName: matched.displayName,
                createEntity: undefined,
                newEntityName: undefined,
            });
        } else if (trimmed) {
            onEditChange({ createEntity: true, newEntityName: trimmed, targetEntityId: undefined, targetEntityName: undefined });
        }
        return true;
    };

    const selectedEntityLabel = edit.createEntity ? edit.newEntityName ?? '' : edit.targetEntityName ?? '';

    const handleClassificationSelect = async (label: string): Promise<boolean> => {
        const option = CLASSIFICATION_OPTIONS.find((o) => o.label === label);
        if (option) onEditChange({ classificationId: option.id });
        return true;
    };

    const selectedClassificationLabel = CLASSIFICATION_OPTIONS.find((o) => o.id === edit.classificationId)?.label ?? '';

    const handleRationaleSelect = async (value: string): Promise<boolean> => {
        onEditChange({ rationale: value });
        return true;
    };

    const canSave =
        !saving &&
        !overLimit &&
        (edit.targetEntityId !== undefined || edit.createEntity || edit.rationale !== undefined || edit.classificationId !== undefined);

    return (
        <div className={styles.Footer}>
            <div className={styles.LeftGroup}>
                <span className={styles.SelectionLabel}>
                    {selectedCount} transaction{selectedCount !== 1 ? 's' : ''} selected
                </span>
                {overLimit && <span className={styles.OverLimitMessage}>Select up to 2000 transactions per edit</span>}
            </div>
            <div className={styles.RightGroup}>
                <div className={styles.Field}>
                    <span className={styles.FieldLabel}>Entity Name</span>
                    <CircularSelect
                        key={entityOptions.length > 0 ? 'entity-loaded' : 'entity-empty'}
                        option={entityOptions}
                        onSelect={handleEntitySelect}
                        selectedOption={selectedEntityLabel}
                        placeholder="Select Entity"
                        selectClassName={styles.FieldSelect}
                        searchable
                        allowAddNew
                        addNewText="Add New Entity"
                        otherOptionPlaceholderText="Enter entity name..."
                        neutralPlaceholder
                        portalPanel
                        onClear={
                            selectedEntityLabel
                                ? () =>
                                      onEditChange({
                                          targetEntityId: undefined,
                                          targetEntityName: undefined,
                                          createEntity: undefined,
                                          newEntityName: undefined,
                                      })
                                : undefined
                        }
                    />
                </div>
                <div className={styles.Field}>
                    <span className={styles.FieldLabel}>Tagging Rationale</span>
                    <CircularSelect
                        option={rationaleOptions}
                        onSelect={handleRationaleSelect}
                        selectedOption={edit.rationale ?? ''}
                        placeholder="Rationale"
                        selectClassName={styles.FieldSelect}
                        searchable
                        allowAddNew
                        addNewText="Add rationale"
                        otherOptionPlaceholderText="Enter rationale..."
                        neutralPlaceholder
                        portalPanel
                        onClear={edit.rationale ? () => onEditChange({ rationale: undefined }) : undefined}
                    />
                </div>
                <div className={styles.Field}>
                    <span className={styles.FieldLabel}>Classification</span>
                    <CircularSelect
                        option={CLASSIFICATION_LABELS}
                        onSelect={handleClassificationSelect}
                        selectedOption={selectedClassificationLabel}
                        placeholder="Classification"
                        selectClassName={styles.FieldSelect}
                        searchable
                        allowAddNew={false}
                        neutralPlaceholder
                        portalPanel
                        onClear={selectedClassificationLabel ? () => onEditChange({ classificationId: undefined }) : undefined}
                    />
                </div>
                <Button type="button" className={styles.CancelBtn} onClick={onCancel} disabled={saving} text="Cancel" />
                <Button type="button" className={styles.SaveBtn} onClick={onSave} disabled={!canSave} text={saving ? 'Saving…' : 'Save'} />
            </div>
        </div>
    );
}

export default DrawerBulkEditFooter;
